import React from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@swyftx/aviary/atoms/Input';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Tooltip } from '@swyftx/aviary/atoms/Tooltip/Tooltip';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { MarginInstrument } from '@swyftx/margin/build/types';

import { Asset } from '@shared/api';
import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { useMarginInstrumentDetails } from 'src/lib/margin/hooks/instruments';
import { useMarginLotsError } from 'src/lib/margin/hooks/trade';
import { useFormatInputText } from 'src/lib/utils/hooks/useFormatInputText';

import { TotalMarginPositionSize } from '../TotalMarginPositionSize';

type Props = {
  asset: Asset;
  lots: string;
  market: MarginInstrument;
  maxMarginPosition: Big;
  onChange: (lots: string) => void;
  readOnly?: boolean;
  disabled?: boolean;
  price?: string;
};

const MarginLotsInput: React.FC<Props> = ({
  market,
  maxMarginPosition,
  lots,
  price,
  asset,
  readOnly,
  disabled,
  onChange,
}) => {
  const { t } = useTranslation('margin');
  const { error } = useMarginLotsError({ lots, market, maxMarginPosition });
  const { priceScale } = useMarginInstrumentDetails({ instrument: market });

  const { formattedAmount, onChangeValue } = useFormatInputText({
    asset,
    value: lots,
    onChange,
    blockAlphaNumericChars: true,
    priceScaleOverride: priceScale,
  });

  return (
    <FlexLayout direction='column' className='w-full' spacing={4}>
      <Input
        variant='monospace'
        readOnly={readOnly}
        disabled={disabled}
        value={formattedAmount}
        error={!!error}
        id='margin-lots'
        onChange={onChangeValue}
        alignment='end'
        secondaryValue={
          price &&
          !disabled && (
            <TotalMarginPositionSize
              lots={lots}
              valueOnly
              price={price}
              size='xsmall'
              weight='none'
              color='secondary'
            />
          )
        }
        leading={
          <Tooltip title={t('tradePanel.tooltips.sizeLots')} side='left' sideOffset={16}>
            <Body className='whitespace-nowrap' color='primary'>
              {t('sizeLots')}
            </Body>
          </Tooltip>
        }
        placeholder={`Min ${formatCurrency(market?.minLots)}`}
        className='text-right text-16 font-medium text-color-text-primary'
      />
      <Body color='error' size='xsmall'>
        {error}
      </Body>
    </FlexLayout>
  );
};

export { MarginLotsInput };
